<template>
  <div class="ai-search">
    <section class="ai-search-hero gutter">
      <div class="ai-search-hero-inner">
        <h1 class="ai-search-heading">
          Describe it. We’ll find it.
        </h1>
        <p class="ai-search-lede">
          Describe the music you need in your own words: the scene, the mood,
          the feeling or a reference to an artist, movie or project. We’ll
          find the tracks that fit your brief.
        </p>

        <form
          class="ai-search-box"
          :class="{ 'is-searching': loading }"
          role="search"
          @submit.prevent="submit"
        >
          <!-- Two layers of the same slowly turning coral gradient: a blurred
               halo behind the box, and a crisp ring at its edge. -->
          <div
            class="ai-search-glow"
            aria-hidden="true"
          />
          <div
            class="ai-search-ring"
            aria-hidden="true"
          />
          <div class="ai-search-field">
            <sparkle-icon class="ai-search-sparkle" />
            <input
              id="ai-search-input"
              ref="input"
              v-model="searchText"
              class="ai-search-input"
              type="search"
              autocomplete="off"
              enterkeyhint="search"
              aria-label="Describe the music you need"
              placeholder="Describe the music you need…"
            >
            <button
              type="submit"
              class="ai-search-button"
              :disabled="searchText.trim().length < minQueryLength"
            >
              Search
            </button>
          </div>
        </form>

        <p class="ai-search-hint">
          Mention mood, genre, setting, energy, instruments, vocals or a
          reference, or anything else that matters.
        </p>

        <div class="ai-search-examples">
          <span class="ai-search-examples-label">Try</span>
          <button
            v-for="example in examples"
            :key="example"
            type="button"
            class="ai-search-example"
            :class="{ 'is-active': example === submittedQuery }"
            @click="runExample(example)"
          >
            {{ example }}
          </button>
        </div>

        <div
          v-if="showFeedback"
          class="ai-search-feedback"
        >
          <span>Were these results helpful?</span>
          <button
            v-for="option in feedbackOptions"
            :key="option.vote"
            type="button"
            class="ai-search-feedback-button"
            :class="{ 'is-selected': vote === option.vote }"
            :aria-label="option.label"
            :aria-pressed="vote === option.vote ? 'true' : 'false'"
            :title="option.label"
            @click="castVote(option.vote)"
          >
            <svg
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path :d="option.path" />
            </svg>
          </button>
          <span
            v-if="vote"
            class="ai-search-feedback-thanks"
          >Thanks!</span>
        </div>
      </div>
    </section>

    <section
      v-if="submittedQuery"
      class="ai-search-results gutter"
    >
      <div class="ai-search-results-inner">
        <div
          v-if="loading"
          class="has-text-centered"
        >
          <span
            class="loader xl"
            style="display: inline-block; width: 3em; height: 3em;"
          />
        </div>

        <div
          v-else-if="results.length > 0"
          class="is-fullwidth"
        >
          <p
            v-if="searchedQuery"
            class="ai-search-searched-as"
          >
            Showing results for <em>{{ searchedQuery }}</em>
          </p>
          <track-list-item
            v-for="track in results"
            :key="track.pk"
            :track="track"
          />
        </div>

        <div
          v-else
          class="columns is-vcentered mt-6"
        >
          <div class="column is-6 is-offset-3 has-text-centered">
            <h3 class="title-wide is-1 text mb-5">
              {{ unavailable ? 'Search is unavailable' : 'No strong match' }}
            </h3>
            <p class="text large mb-4">
              <span v-if="unavailable">
                Searching by description isn’t available right now. You can still
                <router-link :to="{ name: 'track-search' }">browse tracks with filters</router-link>.
              </span>
              <span v-else>
                Nothing in the catalogue is a close match for
                &ldquo;{{ submittedQuery }}&rdquo;. Rather than show you the
                nearest thing we have, we’d rather say so - try describing the
                music itself, or
                <router-link :to="{ name: 'track-search' }">browse with filters</router-link>.
              </span>
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Until a brief is searched, the same featured tracks /tracks opens with. -->
    <section
      v-else-if="featured.length > 0"
      class="ai-search-results gutter"
    >
      <div class="ai-search-results-inner">
        <div class="is-fullwidth">
          <track-list-item
            v-for="track in featured"
            :key="track.pk"
            :track="track"
          />
        </div>
        <div
          v-if="featuredLoading"
          class="has-text-centered"
        >
          <span
            class="loader xl"
            style="display: inline-block; width: 3em; height: 3em;"
          />
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import SparkleIcon from '@/components/SparkleIcon.vue'
import TrackListItem from '@/components/TrackListItem.vue'
import { ApiClient } from '@/api/client.js'
import searchClient from '@/api/trackSearch.js'
import { normalizeTrackSearchResult } from '@/utils/catalogue.js'
import { pageArrival } from '@/utils/pageArrival.js'
import { currentSearchSession } from '@/utils/searchAnalytics.js'

const MIN_QUERY_LENGTH = 2

// Thumbs up and down: outline icons from Feather (MIT).
const FEEDBACK_OPTIONS = [
  {
    vote: 'up',
    label: 'Helpful',
    path: 'M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3'
  },
  {
    vote: 'down',
    label: 'Not helpful',
    path: 'M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17'
  }
]

// For following the featured list's next-page links.
const client = new ApiClient()

// Click-to-run briefs, which double as a demonstration of the kinds of thing
// people can type. Add as many as you like: EXAMPLES_SHOWN of them, picked at
// random, are shown each time the page loads.
const EXAMPLES = [
  'Trailer about a thrilling sports triumph',
  'Similar to Ed Sheeran',
  'Sophisticated, uplifting music for a premium brand film',
  'Upbeat indie pop music for a travel vlog',
  'I need a distinctive theme for a true crime podcast',
  'Music for a premium skincare advert',
  '80s cinematic synths for a futuristic technology advert',
  'Emotional scene of a bride walking down the aisle',
  'Ambient background texture to build suspense',
  'Warm, hopeful piano for a charity film',
  'Similar to music in "Interstellar"',
  'Similar to music in "Lord of the Rings"',
  'Futuristic car advert, should be instrumental'
]
const EXAMPLES_SHOWN = 3

// `count` items from `items` in random order, without repeats.
function pickRandom (items, count) {
  const shuffled = items.slice()
  // Fisher-Yates: an unbiased shuffle, unlike sorting by Math.random().
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    const swap = shuffled[i]
    shuffled[i] = shuffled[j]
    shuffled[j] = swap
  }
  return shuffled.slice(0, count)
}

export default {
  name: 'AiTrackSearch',

  components: {
    SparkleIcon,
    TrackListItem
  },

  data () {
    return {
      // Picked once per page load; a search doesn't reshuffle them.
      examples: pickRandom(EXAMPLES, EXAMPLES_SHOWN),
      minQueryLength: MIN_QUERY_LENGTH,
      searchText: '',
      submittedQuery: '',
      results: [],
      // Set when the query as typed found no strong match and the backend
      // retried it as "music for <query>": the query those results are for.
      searchedQuery: '',
      loading: false,
      unavailable: false,
      // The current search's id in the backend's search log, which a thumbs
      // up or down is recorded against, and the vote given on it.
      searchId: null,
      vote: null,
      // The vote the server last confirmed for this search, to fall back to
      // if a later one fails.
      savedVote: null,
      feedbackOptions: FEEDBACK_OPTIONS,
      // Clicking examples in quick succession sends overlapping requests;
      // only the latest one may update the page or the player queue.
      requestId: 0,
      // The featured list. Kept here rather than in the trackSearch store so
      // loading it doesn't reset or replace the /tracks page's filters and
      // results.
      featured: [],
      featuredNextUrl: null,
      featuredLoading: false
    }
  },

  computed: {
    // Only when there are results to rate, and the search was logged.
    showFeedback: function () {
      return Boolean(
        this.submittedQuery && !this.loading && this.results.length > 0 && this.searchId
      )
    }
  },

  mounted () {
    // ?q= makes a search shareable and survives a refresh. A refresh, or Back
    // to here, runs the search again: logged as such, not as a shared link.
    const query = (this.$route.query.q || '').trim()
    if (query.length >= MIN_QUERY_LENGTH) {
      this.searchText = query
      this.search(query, pageArrival() === 'reload' ? 'reload' : 'link')
    } else {
      this.loadFeatured()
      this.$nextTick(() => {
        if (this.$refs.input) this.$refs.input.focus()
      })
    }
  },

  created () {
    window.addEventListener('scroll', this.handleScroll)
    // Vote sending order, and a counter to tell the latest click from
    // earlier ones. Neither is shown, so neither needs to be reactive.
    this.voteQueue = Promise.resolve()
    this.voteId = 0
  },

  destroyed () {
    window.removeEventListener('scroll', this.handleScroll)
  },

  methods: {
    loadFeatured: function () {
      // A track search with no filters returns tracks in featured order;
      // after the first page, follow the API's next link.
      const isFirstPage = this.featured.length === 0
      this.featuredLoading = true
      const request = isFirstPage
        // Marked so the backend doesn't log it as a track search.
        ? searchClient.searchTracks({ source: 'ai_featured' })
        : client.get(this.featuredNextUrl)

      return request
        .then((response) => {
          // A brief may have been searched while this was loading; the player
          // queue then belongs to its results.
          if (this.submittedQuery) return
          const tracks = response.data.results.map(track => normalizeTrackSearchResult(track))
          this.featured = isFirstPage ? tracks : [...this.featured, ...tracks]
          this.featuredNextUrl = response.data.next
          if (this.featured.length > 0) {
            this.$store.dispatch('player/loadTracks', this.featured)
          }
        })
        .catch(() => {
          // The featured list is a nicety; without it the page still works.
          this.featuredNextUrl = null
        })
        .finally(() => {
          this.featuredLoading = false
        })
    },

    handleScroll: function () {
      // Infinite scroll for the featured list only: brief results are a
      // fixed set.
      if (this.submittedQuery || this.featuredNextUrl === null || this.featuredLoading) {
        return
      }
      const bottomOfWindow = (
        Math.round(document.documentElement.scrollTop) + window.innerHeight >=
        document.documentElement.offsetHeight
      )
      if (bottomOfWindow) {
        this.loadFeatured()
      }
    },

    submit: function () {
      const query = this.searchText.trim()
      if (query.length < MIN_QUERY_LENGTH) {
        return
      }
      if (this.$refs.input) this.$refs.input.blur()
      this.search(query, 'typed')
    },

    runExample: function (example) {
      this.searchText = example
      this.search(example, 'example')
    },

    // source - 'typed', 'example' or 'link' - is recorded with the search
    // in the backend's search log, to tell real briefs from example clicks.
    search: function (query, source) {
      this.syncRouteQuery(query)

      const requestId = ++this.requestId
      this.submittedQuery = query
      this.loading = true
      this.unavailable = false
      this.searchId = null
      this.searchedQuery = ''
      this.vote = null
      this.savedVote = null

      return searchClient.searchTracksNaturalLanguage({ q: query, source, session: currentSearchSession() })
        .then((response) => {
          if (requestId !== this.requestId) return
          this.searchId = response.data.search_id || null
          // Differs from the query only when a retry as "music for ..." found
          // the results shown.
          const searched = response.data.searched_query
          this.searchedQuery = searched && searched !== response.data.query ? searched : ''
          // Same normaliser as the faceted search, so TrackListItem and the
          // player see identical objects either way.
          this.results = response.data.results.map(track => normalizeTrackSearchResult(track))
          if (this.results.length > 0) {
            this.$store.dispatch('player/loadTracks', this.results)
          }
        })
        .catch((error) => {
          if (requestId !== this.requestId) return
          // 503 is the endpoint saying the embedding provider is down;
          // anything else (including a 404 from the flag being turned off
          // mid-session) lands in the same honest empty state.
          this.results = []
          this.unavailable = error.response ? error.response.status === 503 : true
        })
        .finally(() => {
          if (requestId !== this.requestId) return
          this.loading = false
        })
    },

    castVote: function (vote) {
      // Clicking the selected thumb again clears the vote.
      const newVote = this.vote === vote ? null : vote
      const searchId = this.searchId
      const voteId = ++this.voteId
      this.vote = newVote

      // Votes go out one at a time, in click order, so quick clicks can't
      // reach the server out of order and store an older vote than the one
      // on screen.
      this.voteQueue = this.voteQueue
        .then(() => searchClient.voteOnNaturalLanguageSearch(searchId, newVote))
        .then(() => {
          if (this.searchId === searchId) {
            this.savedVote = newVote
          }
        })
        .catch(() => {
          // Only the latest click decides what's shown: an earlier failure
          // is overtaken by the votes queued behind it. Fall back to what the
          // server last saved, unless a new search has replaced this one.
          if (voteId === this.voteId && this.searchId === searchId) {
            this.vote = this.savedVote
          }
        })
    },

    syncRouteQuery: function (query) {
      // Rewrites the address bar without telling the router. App.vue keys
      // <router-view> on $route.fullPath, so a router.replace() here would
      // destroy this page mid-search and mount a fresh one, which runs the
      // search again from ?q= - a second paid request - and the router jumps
      // the page back to the top. On a load or refresh the router still
      // reads ?q= from the real URL.
      const url = new URL(window.location.href)
      if ((url.searchParams.get('q') || '') === query) return
      if (query) {
        url.searchParams.set('q', query)
      } else {
        url.searchParams.delete('q')
      }
      // Keep history.state: vue-router stores its scroll positions there.
      // replaceState, not pushState: each search shouldn't become a history entry.
      window.history.replaceState(window.history.state, '', url.toString())
    }
  },

  metaInfo () {
    return {
      title: 'Search by description',
      // A flagged test page, not something to index.
      meta: [{ name: 'robots', content: 'noindex, nofollow' }]
    }
  }
}
</script>

<style lang="scss" scoped>
@import "../assets/scss/_variables.scss";

// The coral family the glow turns through: Sonata coral plus warmer and
// pinker neighbours, so the light moves without leaving the brand.
$coral: $red;
$coral-pink: rgb(255, 122, 168);
$coral-peach: rgb(255, 160, 122);
$coral-gold: rgb(255, 196, 120);

$glow-gradient: conic-gradient(
  from 0deg,
  $coral,
  $coral-pink,
  $coral-peach,
  $coral-gold,
  $coral-pink,
  $coral
);

.ai-search-hero {
  position: relative;
  padding: 0 0 40px 0;

  // A soft, slowly breathing wash of coral behind the whole hero, so the
  // colour isn't confined to the box.
  &::before {
    content: "";
    position: absolute;
    left: 50%;
    top: 40%;
    width: 100%;
    max-width: 1100px;
    height: 420px;
    transform: translate(-50%, -50%);
    background:
      radial-gradient(closest-side at 35% 55%, rgba($coral, 0.08), transparent),
      radial-gradient(closest-side at 65% 45%, rgba($coral-pink, 0.06), transparent);
    filter: blur(20px);
    pointer-events: none;
    animation: ai-breathe 9s ease-in-out infinite;
  }
}

.ai-search-hero-inner {
  position: relative;
  max-width: 820px;
  margin: 0 auto;
  text-align: center;
}

.ai-search-heading {
  color: $bluegrey1;
  font-size: 48px;
  font-weight: 500;
  letter-spacing: -0.02em;
  line-height: 1.05;
  margin-bottom: 16px;
  text-wrap: balance;

  @media only screen and (max-width: $tablet) {
    font-size: 32px;
  }
}

.ai-search-lede {
  color: $bluegrey2;
  font-size: $size-5;
  line-height: 1.5;
  // About the search box's width, so the intro sits on two lines above it.
  max-width: 41em;
  margin: 0 auto 28px !important;
  text-wrap: balance;

  @media only screen and (max-width: $tablet) {
    margin-bottom: 24px !important;
  }
}

// The search box.

.ai-search-box {
  position: relative;
  max-width: 700px;
  margin: 0 auto;
  isolation: isolate;
  border-radius: 999px;
}

.ai-search-glow,
.ai-search-ring {
  position: absolute;
  border-radius: 999px;
  overflow: hidden;
  pointer-events: none;
  z-index: -1;

  // An oversized square of conic gradient spinning behind the pill. The
  // parent clips it to the pill's shape; only the colour at the edge shows.
  &::before {
    content: "";
    position: absolute;
    left: 50%;
    top: 50%;
    width: 150%;
    aspect-ratio: 1;
    background: $glow-gradient;
    transform: translate(-50%, -50%) rotate(0deg);
    animation: ai-spin 14s linear infinite;
  }
}

.ai-search-glow {
  inset: -4px;
  filter: blur(10px);
  opacity: 0.3;
  transition: opacity 0.4s ease, inset 0.4s ease;
}

.ai-search-ring {
  inset: -1.5px;
  opacity: 0.75;
}

.ai-search-box:focus-within .ai-search-glow {
  opacity: 0.45;
  inset: -6px;
}

.ai-search-box.is-searching {
  .ai-search-glow {
    opacity: 0.55;
    animation: ai-pulse 1.4s ease-in-out infinite;
  }
  .ai-search-glow::before,
  .ai-search-ring::before {
    animation-duration: 3s;
  }
}

.ai-search-field {
  display: flex;
  align-items: center;
  gap: 12px;
  background: $white;
  border-radius: 999px;
  padding: 6px 6px 6px 20px;
  min-height: 58px;

  @media only screen and (max-width: $tablet) {
    padding-left: 16px;
    min-height: 52px;
    gap: 8px;
  }
}

.ai-search-sparkle {
  width: 20px;
  height: 20px;
}

.ai-search-input {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  box-shadow: none;
  color: $bluegrey0;
  caret-color: $coral;
  font-family: $family-sans-serif;
  font-size: 17px;
  padding: 6px 0;
  -webkit-appearance: none;

  &::placeholder {
    color: $bluegrey4;
  }
  @media only screen and (max-width: $tablet) {
    font-size: $size-5;
  }
}

.ai-search-button {
  flex: none;
  border: none;
  border-radius: 999px;
  background: $bluegrey1;
  color: $white;
  cursor: pointer;
  font-family: $family-sans-serif;
  font-size: $size-5;
  font-weight: 500;
  padding: 0 22px;
  height: 46px;
  transition: background 0.2s ease, opacity 0.2s ease;

  &:hover:not(:disabled),
  &:focus-visible {
    background: $bluegrey0;
  }
  &:focus-visible {
    outline: 2px solid $coral;
    outline-offset: 2px;
  }
  &:disabled {
    cursor: default;
    opacity: 0.35;
  }

  @media only screen and (max-width: $tablet) {
    height: 40px;
    padding: 0 16px;
    font-size: $size-6;
  }
}

.ai-search-hint {
  color: $bluegrey3;
  font-size: $size-6;
  margin: 18px 0 32px !important;
}

.ai-search-feedback {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 20px;
  gap: 4px;
  color: $bluegrey2;
  font-size: $size-6;

  > span:first-child {
    margin-right: 4px;
  }
}

.ai-search-feedback-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 999px;
  background: transparent;
  color: $bluegrey3;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;

  svg {
    width: 18px;
    height: 18px;
    fill: none;
    stroke: currentColor;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  &:hover,
  &:focus-visible {
    background: rgba($coral, 0.1);
    color: $coral;
    outline: none;
  }

  &.is-selected {
    color: $coral;
    svg {
      fill: rgba($coral, 0.25);
    }
  }
}

.ai-search-feedback-thanks {
  color: $coral;
  margin-left: 4px;
}

// Examples.

.ai-search-examples {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 8px;

  // Wider than the search box on desktop, so the examples sit on one line.
  // Only from desktop up: below that, the extra width would push past the
  // page gutter, so the row wraps instead.
  @media only screen and (min-width: $desktop) {
    margin: 0 -60px;
  }
}

.ai-search-examples-label {
  color: $bluegrey3;
  font-size: 13px;
  margin-right: 2px;
}

.ai-search-example {
  border: 1px solid $bluegrey5;
  border-radius: 999px;
  background: rgba($white, 0.85);
  color: $bluegrey1;
  cursor: pointer;
  font-family: $family-sans-serif;
  font-size: 13px;
  line-height: 1.3;
  padding: 6px 14px;
  transition: border-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;

  &:hover,
  &:focus-visible,
  &.is-active {
    border-color: $coral;
    color: darken($coral, 18%);
    box-shadow: 0 0 0 3px rgba($coral, 0.15);
    outline: none;
  }
}

// Results.

.ai-search-results {
  padding-top: 24px;
}

// "Showing results for music for …" above a retried search's results, in
// the same voice as "Were these results helpful?". Pulled up into the
// results section's top padding, with more room before the first track.
.ai-search-searched-as {
  margin-top: -34px;
  margin-bottom: 28px;
  color: $bluegrey2;
  font-size: $size-6;

  em {
    color: inherit;
  }
}

// The results and featured list sit in a centred column rather than running
// the full width of the page.
.ai-search-results-inner {
  max-width: 1100px;
  margin: 0 auto;
}

@keyframes ai-spin {
  to { transform: translate(-50%, -50%) rotate(360deg); }
}

@keyframes ai-breathe {
  0%, 100% { opacity: 0.7; transform: translate(-50%, -50%) scale(1); }
  50% { opacity: 1; transform: translate(-50%, -50%) scale(1.06); }
}

@keyframes ai-pulse {
  0%, 100% { opacity: 0.35; }
  50% { opacity: 0.6; }
}

@media (prefers-reduced-motion: reduce) {
  .ai-search-hero::before,
  .ai-search-glow,
  .ai-search-glow::before,
  .ai-search-ring::before,
  .ai-search-box.is-searching .ai-search-glow {
    animation: none !important;
  }
}
</style>
